import { useMemo, useState } from 'react';

export const useOrdering = <T extends Record<string, any>>(
    item: T[],
    defaultSortKey: string,
    initialOrderForDefaultKey: 'asc' | 'desc' = 'asc'
) => {
    const [orderMap, setOrderMap] = useState<Record<string, 'asc' | 'desc'>>({
        [defaultSortKey]: initialOrderForDefaultKey
    });
    const [orderBy, setOrderBy] = useState<string>(defaultSortKey);

    const handleRequestSort = (property: string) => {
        const currentOrder = orderMap[property] || 'asc';
        const newOrder = currentOrder === 'asc' ? 'desc' : 'asc';
        setOrderMap((prev) => ({ ...prev, [property]: newOrder }));
        setOrderBy(property);
    };

    const order = orderMap[orderBy] || 'asc';

    const orderedItems = useMemo(() => {
        return [...item].sort((a, b) => {
            let aValue = a[orderBy];
            let bValue = b[orderBy];

            // Extract sortable values from complex structures
            const getSortValue = (value: any): string | null | undefined => {
                if (value === null || value === undefined) return value;

                // Handle arrays - use first element
                if (Array.isArray(value)) {
                    if (value.length === 0) return undefined;
                    return getSortValue(value[0]);
                }

                // Handle objects - convert to string
                if (typeof value === 'object') {
                    return JSON.stringify(value);
                }

                // Return primitive values as-is
                return value;
            };

            aValue = getSortValue(aValue);
            bValue = getSortValue(bValue);

            // Handle null/undefined values based on sort direction
            // asc: null/undefined at bottom, desc: null/undefined at top
            if (
                (aValue === null || aValue === undefined) &&
                (bValue === null || bValue === undefined)
            )
                return 0;
            if (aValue === null || aValue === undefined)
                return order === 'asc' ? 1 : -1;
            if (bValue === null || bValue === undefined)
                return order === 'asc' ? -1 : 1;

            // Convert to strings for consistent comparison
            const aStr = String(aValue).toLowerCase();
            const bStr = String(bValue).toLowerCase();

            if (aStr < bStr) {
                return order === 'asc' ? -1 : 1;
            }
            if (aStr > bStr) {
                return order === 'asc' ? 1 : -1;
            }
            return 0;
        });
    }, [item, orderBy, order]);

    return {
        order,
        orderBy,
        handleRequestSort,
        orderedItems,
        orderMap
    };
};
